class AaAeropadsSoundpacks extends HTMLElement {
  connectedCallback() {
    this.audio = null;
    this.activeButton = null;
    this.patchPreviewVoices = new Map();
    this.patchPreviewFadeInDuration = Math.max(0, Number(this.dataset.aaPatchPreviewFadeInDuration) || 1500);
    this.patchPreviewFadeOutDuration = Math.max(0, Number(this.dataset.aaPatchPreviewFadeOutDuration) || 3000);
    this.status = this.querySelector('[data-aa-soundpacks-status]');
    this.handleClick = this.handleClick.bind(this);
    this.handleExternalStop = this.handleExternalStop.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.addEventListener('click', this.handleClick);
    window.addEventListener('aa:soundpacks-stop-preview', this.handleExternalStop);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('aa:soundpacks-stop-preview', this.handleExternalStop);
    window.removeEventListener('pagehide', this.handlePageHide);
    this.stopPreview();
    this.stopAllPatchPreviews();
  }

  handleClick(event) {
    const patchPreviewButton = event.target.closest('[data-aa-patch-preview]');

    if (patchPreviewButton && this.contains(patchPreviewButton)) {
      event.preventDefault();
      this.togglePatchPreview(patchPreviewButton);
      return;
    }

    const button = event.target.closest('[data-aa-soundpacks-preview]');

    if (!button || !this.contains(button)) return;

    event.preventDefault();
    this.fadeOutAllPatchPreviews();
    this.togglePreview(button);
  }

  handleExternalStop(event) {
    if (event.detail === this) return;

    this.stopPreview();
    this.fadeOutAllPatchPreviews();
  }

  handlePageHide() {
    this.stopPreview();
    this.stopAllPatchPreviews();
  }

  togglePatchPreview(button) {
    const currentVoice = this.patchPreviewVoices.get(button);

    if (currentVoice?.active) {
      this.fadeOutPatchPreview(button);
      return;
    }

    window.dispatchEvent(new CustomEvent('aa:soundpacks-stop-preview', { detail: this }));
    this.stopPreview();
    this.fadeOutAllPatchPreviews(button);
    this.startPatchPreview(button);
  }

  startPatchPreview(button) {
    const url = button.dataset.previewUrl;
    const patch = button.closest('.aa-aeropads-soundpacks__patch');

    if (!url || !patch) return;

    let voice = this.patchPreviewVoices.get(button);

    if (!voice) {
      const audio = new Audio(url);
      audio.loop = true;
      audio.preload = 'none';
      audio.volume = 0;

      voice = {
        active: false,
        animationFrame: null,
        audio,
        button,
        generation: 0,
        patch,
      };

      voice.handleError = () => {
        if (this.patchPreviewVoices.get(button) !== voice) return;

        this.setStatus(button.dataset.errorMessage);
        this.cleanupPatchPreviewVoice(voice);
      };

      audio.addEventListener('error', voice.handleError);
      this.patchPreviewVoices.set(button, voice);
    }

    voice.active = true;
    voice.generation += 1;
    const generation = voice.generation;
    this.cancelPatchPreviewFade(voice);
    this.startPatchPreviewGlow(voice);
    this.setPatchPreviewState(voice, false, true);

    const beginFade = () => {
      if (this.patchPreviewVoices.get(button) !== voice) return;
      if (!voice.active || voice.generation !== generation) return;

      this.setPatchPreviewState(voice, true, false);
      this.setStatus(button.dataset.playingMessage);
      this.fadePatchPreviewVoice(voice, 1, false);
    };

    if (!voice.audio.paused) {
      beginFade();
      return;
    }

    const playAttempt = voice.audio.play();

    if (playAttempt && typeof playAttempt.then === 'function') {
      playAttempt.then(beginFade).catch(() => {
        if (this.patchPreviewVoices.get(button) !== voice) return;
        if (voice.generation !== generation) return;

        this.setStatus(button.dataset.errorMessage);
        this.cleanupPatchPreviewVoice(voice);
      });
    } else {
      beginFade();
    }
  }

  fadeOutPatchPreview(button) {
    const voice = this.patchPreviewVoices.get(button);

    if (!voice) return;

    voice.active = false;
    voice.generation += 1;
    this.setPatchPreviewState(voice, false, false);
    this.fadePatchPreviewVoice(voice, 0, true);
  }

  fadeOutAllPatchPreviews(exceptButton = null) {
    if (!this.patchPreviewVoices) return;

    for (const [button, voice] of this.patchPreviewVoices) {
      if (button !== exceptButton && voice.active) this.fadeOutPatchPreview(button);
    }
  }

  fadePatchPreviewVoice(voice, targetVolume, stopAfterFade) {
    this.cancelPatchPreviewFade(voice);

    const startVolume = voice.audio.volume;
    const volumeChange = targetVolume - startVolume;
    const fadeDuration =
      targetVolume > startVolume ? this.patchPreviewFadeInDuration : this.patchPreviewFadeOutDuration;
    const startTime = performance.now();

    const finish = () => {
      voice.animationFrame = null;
      voice.audio.volume = targetVolume;
      this.setPatchPreviewGlowLevel(voice, targetVolume);

      if (stopAfterFade && !voice.active) {
        this.setStatus(voice.button.dataset.pausedMessage);
        this.cleanupPatchPreviewVoice(voice);
      }
    };

    if (fadeDuration === 0 || Math.abs(volumeChange) < 0.001) {
      finish();
      return;
    }

    const step = (currentTime) => {
      if (this.patchPreviewVoices.get(voice.button) !== voice) return;

      const progress = Math.min((currentTime - startTime) / fadeDuration, 1);
      voice.audio.volume = Math.min(1, Math.max(0, startVolume + volumeChange * progress));
      this.setPatchPreviewGlowLevel(voice, voice.audio.volume);

      if (progress < 1) {
        voice.animationFrame = requestAnimationFrame(step);
      } else {
        finish();
      }
    };

    voice.animationFrame = requestAnimationFrame(step);
  }

  cancelPatchPreviewFade(voice) {
    if (voice.animationFrame === null) return;

    cancelAnimationFrame(voice.animationFrame);
    voice.animationFrame = null;
  }

  cleanupPatchPreviewVoice(voice) {
    this.cancelPatchPreviewFade(voice);
    this.setPatchPreviewState(voice, false, false);
    this.stopPatchPreviewGlow(voice);
    voice.audio.removeEventListener('error', voice.handleError);
    voice.audio.pause();

    try {
      voice.audio.currentTime = 0;
    } catch {}

    voice.audio.removeAttribute('src');
    voice.audio.load();

    if (this.patchPreviewVoices.get(voice.button) === voice) {
      this.patchPreviewVoices.delete(voice.button);
    }
  }

  setPatchPreviewState(voice, isPlaying, isLoading) {
    voice.patch.classList.toggle('is-audio-playing', isPlaying);
    voice.patch.classList.toggle('is-audio-loading', isLoading);
    voice.button.setAttribute('aria-pressed', String(isPlaying));
    voice.button.setAttribute(
      'aria-label',
      isPlaying ? voice.button.dataset.pauseLabel : voice.button.dataset.previewLabel,
    );

    if (isLoading) {
      voice.button.setAttribute('aria-busy', 'true');
    } else {
      voice.button.removeAttribute('aria-busy');
    }
  }

  startPatchPreviewGlow(voice) {
    const flowDuration = 5200 + Math.round(Math.random() * 3200);
    const flickerDuration = 6100 + Math.round(Math.random() * 3600);

    voice.patch.style.setProperty('--aa-patch-flame-flow-duration', `${flowDuration}ms`);
    voice.patch.style.setProperty('--aa-patch-flame-flicker-duration', `${flickerDuration}ms`);
    voice.patch.style.setProperty('--aa-patch-flame-flow-delay', `${-Math.round(Math.random() * flowDuration)}ms`);
    voice.patch.style.setProperty(
      '--aa-patch-flame-flicker-delay',
      `${-Math.round(Math.random() * flickerDuration)}ms`,
    );
    voice.patch.classList.add('is-audio-glowing');
    this.setPatchPreviewGlowLevel(voice, voice.audio.volume);
  }

  setPatchPreviewGlowLevel(voice, volume) {
    voice.patch.style.setProperty('--aa-patch-audio-level', String(Math.min(1, Math.max(0, volume))));
  }

  stopPatchPreviewGlow(voice) {
    voice.patch.classList.remove('is-audio-glowing');
    voice.patch.style.removeProperty('--aa-patch-audio-level');
    voice.patch.style.removeProperty('--aa-patch-flame-flow-duration');
    voice.patch.style.removeProperty('--aa-patch-flame-flicker-duration');
    voice.patch.style.removeProperty('--aa-patch-flame-flow-delay');
    voice.patch.style.removeProperty('--aa-patch-flame-flicker-delay');
  }

  stopAllPatchPreviews() {
    if (!this.patchPreviewVoices) return;

    for (const voice of this.patchPreviewVoices.values()) {
      this.cleanupPatchPreviewVoice(voice);
    }
  }

  togglePreview(button) {
    const url = button.dataset.previewUrl;

    if (!url) return;

    if (this.audio && this.activeButton === button && !this.audio.paused) {
      this.audio.pause();
      return;
    }

    window.dispatchEvent(new CustomEvent('aa:soundpacks-stop-preview', { detail: this }));
    this.stopPreview();

    const audio = new Audio(url);
    audio.preload = 'none';
    this.audio = audio;
    this.activeButton = button;

    audio.addEventListener('play', () => {
      this.setButtonState(button, true);
      this.setStatus(button.dataset.playingMessage);
    });

    audio.addEventListener('pause', () => {
      if (!audio.ended) {
        this.setButtonState(button, false);
        this.setStatus(button.dataset.pausedMessage);
      }
    });

    audio.addEventListener('ended', () => {
      this.setButtonState(button, false);
      this.setStatus(button.dataset.pausedMessage);
    });

    audio.addEventListener('error', () => {
      this.setButtonState(button, false);
      this.setStatus(button.dataset.errorMessage);
    });

    const playAttempt = audio.play();

    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {
        this.setButtonState(button, false);
        this.setStatus(button.dataset.errorMessage);
      });
    }
  }

  stopPreview() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }

    if (this.activeButton) this.setButtonState(this.activeButton, false);

    this.audio = null;
    this.activeButton = null;
  }

  setButtonState(button, isPlaying) {
    button.textContent = isPlaying ? button.dataset.pauseLabel : button.dataset.previewLabel;
    button.setAttribute('aria-pressed', String(isPlaying));
  }

  setStatus(message) {
    if (this.status && message) this.status.textContent = message;
  }
}

if (!customElements.get('aa-aeropads-soundpacks')) {
  customElements.define('aa-aeropads-soundpacks', AaAeropadsSoundpacks);
}
